import { LinkSize, Tooltip, TooltipTrigger } from '@vizit/atoms';
import { BookSharpIcon, ChevronRightIcon, CompassIcon, Home01Icon, type IconProps } from '@vizit/icons';
import { cva, cx } from '@vizit/theme';
import type { ComponentType } from 'react';
import { createElement, isValidElement } from 'react';
import { Breadcrumbs as AriaBreadcrumbs, Breadcrumb, Focusable } from 'react-aria-components';
import { RouterLink } from '../router-link';
import type { BreadcrumbsProps } from './types';

/**
 * Maps breadcrumb hrefs to their corresponding icon components
 * Icons are added at render time to avoid serialization issues with browser history
 */
const BREADCRUMB_ICON_MAP: Record<string, ComponentType<IconProps>> = {
  '/': Home01Icon,
  '/library': BookSharpIcon,
  '/explore': CompassIcon,
};

// Hrefs in the app are workspace-scoped (/w/{slug}/library); the icon map is
// keyed on the bare path so icons survive serialized trails, where component
// references cannot travel.
const iconMapKey = (href: string) => href.replace(/^\/w\/[^/]+(?=\/|$)/, '') || '/';

const getIconSize = (linkSize: LinkSize): number => {
  switch (linkSize) {
    case LinkSize.Sm:
      return 16;
    case LinkSize.Md:
      return 20;
    case LinkSize.Lg:
      return 24;
    default:
      return 16;
  }
};

const breadcrumbThumbnailClasses = cva({
  // 36px box = 32px image + 1px gap + 1px border on every side; border and
  // gap are always present (transparent when not current) so the image never
  // resizes between states. The hover overlay insets past border + gap so it
  // darkens only the image.
  base: [
    'relative block h-18 w-18 shrink-0 overflow-hidden border bg-primary p-px no-underline outline-none',
    'focus-visible:ring-2 focus-visible:ring-gray-modern-500',
  ],
  variants: {
    isCurrent: {
      // Current image: bordered, not clickable
      true: 'border-brand-secondary cursor-default',
      // Clickable thumbnail: darken on hover/focus
      false:
        'border-transparent after:absolute after:inset-[2px] after:bg-black/20 after:opacity-0 hover:after:opacity-100 focus-visible:after:opacity-100',
    },
  },
  defaultVariants: {
    isCurrent: false,
  },
});

const breadcrumbLinkClasses = cva({
  base: [
    'text-gray-utility-500 font-semibold overflow-hidden text-ellipsis whitespace-nowrap no-underline py-2 px-4 outline-none',
    'focus-visible:ring-2 focus-visible:ring-gray-modern-500 focus-visible:text-gray-utility-700',
  ],
  variants: {
    isCurrent: {
      true: 'bg-primary-hover cursor-default',
      false: 'hover:bg-primary-hover hover:text-gray-utility-700 hover:underline',
    },
    isDisabled: {
      true: 'cursor-not-allowed text-disabled bg-disabled-subtle',
      false: '',
    },
  },
  defaultVariants: {
    isCurrent: false,
    isDisabled: false,
  },
});

export function Breadcrumbs(props: BreadcrumbsProps) {
  const { items, maxLabelWidth = 280, className, size = LinkSize.Sm, ...rest } = props;

  if (items.length < 2) return null;

  return (
    <AriaBreadcrumbs className={cx('flex items-center gap-1', className)} {...rest}>
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const isCurrent = isLast && !item.href;
        const isThumbnail = !!item.thumbnailSrc;
        // Captured so the state updater's closure keeps the narrowed type.
        const itemTrail = item.trail;
        const iconSize = getIconSize(size);

        // Map icon from href if not already present
        // This ensures icons are only added at render time, never stored in navigation state
        const icon = item.icon || (item.href ? BREADCRUMB_ICON_MAP[iconMapKey(item.href)] : undefined);

        const renderContent = () => {
          if (item.thumbnailSrc) {
            // Decorative: the accessible name lives on the trigger (link aria-label / span role="img")
            return <img src={item.thumbnailSrc} alt="" className="h-full w-full object-cover" />;
          }

          if (!icon) return item.label;

          const iconElement = isValidElement(icon)
            ? icon
            : createElement(icon as React.ComponentType<IconProps>, {
                size: iconSize,
              });

          return (
            <span className="flex items-center gap-4">
              {iconElement}
              <span>{item.label}</span>
            </span>
          );
        };

        const crumb =
          isCurrent || !item.href ? (
            <span
              aria-current={isCurrent ? 'page' : undefined}
              // Thumbnails carry their name themselves and stay keyboard-reachable
              // so the filename tooltip is available without a pointer
              role={isThumbnail ? 'img' : undefined}
              aria-label={isThumbnail ? item.label : undefined}
              tabIndex={isThumbnail ? 0 : undefined}
              className={
                isThumbnail
                  ? cx(breadcrumbThumbnailClasses({ isCurrent }), 'cursor-default')
                  : cx(
                      breadcrumbLinkClasses({ isCurrent, isDisabled: !!item.isDisabled }),
                      'cursor-default',
                      size === LinkSize.Sm ? 'text-sm' : size === LinkSize.Md ? 'text-md' : 'text-lg',
                    )
              }
              style={isThumbnail ? undefined : { maxWidth: maxLabelWidth }}
            >
              {renderContent()}
            </span>
          ) : (
            <RouterLink
              aria-label={item.label}
              size={size}
              to={item.href}
              search={item.search}
              state={itemTrail ? (previous) => ({ ...previous, from: { trail: itemTrail } }) : undefined}
              className={
                isThumbnail
                  ? breadcrumbThumbnailClasses({ isCurrent: false })
                  : breadcrumbLinkClasses({ isCurrent: false, isDisabled: !!item.isDisabled })
              }
              isDisabled={!!item.isDisabled}
              style={isThumbnail ? undefined : { maxWidth: maxLabelWidth }}
            >
              {renderContent()}
            </RouterLink>
          );

        return (
          <Breadcrumb
            // biome-ignore lint/suspicious/noArrayIndexKey: this is fine
            key={index}
            className="flex items-center gap-2 text-gray-utility-500"
          >
            {isThumbnail ? (
              <TooltipTrigger delay={200}>
                {/* The current crumb is a plain span; Focusable lets it drive the tooltip */}
                {isCurrent || !item.href ? <Focusable>{crumb}</Focusable> : crumb}
                <Tooltip>{item.label}</Tooltip>
              </TooltipTrigger>
            ) : (
              crumb
            )}
            {!isLast && <ChevronRightIcon size={iconSize} />}
          </Breadcrumb>
        );
      })}
    </AriaBreadcrumbs>
  );
}
